import {Organization} from '@mintdata-wsp/common/state/DataState';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {ConfigUtil} from '@mintdata-wsp/common/util/ConfigUtil';
import {WindowLocationUtil} from '@mintdata-wsp/common/util/WindowLocationUtil';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {paths} from '@mintdata-wsp/wsp/ui/routes/Routing';
import {SettingsRoutes} from '@mintdata-wsp/wsp/ui/views/workspace/settings/SettingsPanel';
import {LocalStateUtil} from '@mintdata-wsp/wsp/util/LocalStateUtil';
import {PublishedAppUrlUtil} from '@mintdata-wsp/wsp/util/PublishedAppUrlUtil';
import {Category, mk} from '@mintdata/mint-kit/api/log/mk';
import {History, Pathname} from 'history';
import {action} from 'mobx';
import {RouteComponentProps} from 'react-router';

export class NavigationController
{
    @action.bound
    public toOrganizationSettings(organization?:Organization):void
    {
        const path = paths.settings();
        this._navigateWithPossibleRedirect(path, organization);
    }

    @action.bound
    public toOrganizationSettingsSpaces():void
    {
        const path = paths.settings();
        controllers.navigation.navigateTo(`${path}/${SettingsRoutes.Spaces}`);
    }

    public toDesign(projectId:number, designId:number, isMultiplayerMode:boolean = true):void
    {
        this._navigateTo(`${paths.design(projectId, designId, isMultiplayerMode)}`);
    }

    public toCurrentDesign():void
    {
        const currentRouteProps = rootState.navigation.currentRouteProps;
        const {projectId, designId} = currentRouteProps.match.params;

        this.toDesign(projectId, designId, rootState.ui.visualDesigner.openDesign.isMultiplayerMode
            || location.hash.indexOf('multiplayer=no') === -1);
    }

    public toFlowEditor(projectId:number, designId:number, functionId:number):void
    {
        this._navigateTo(`${paths.flowEditor(projectId, designId, functionId, rootState.ui.visualDesigner.openDesign.isMultiplayerMode)}`);
    }

    public toAppHome(organization?:Organization):void
    {
        const toOrganization = organization || rootState.userSession.currentOrganization;
        const orgSid = toOrganization.siteId;
        const selectedProject = LocalStateUtil.getSelectedProject(orgSid);
        const existingProject = rootState.data.projects.find(p => p.id === selectedProject);

        controllers.ui.openDesign.takeDesignScreenshot();

        if( selectedProject && existingProject )
        {
            this.toProject(selectedProject);
        }
        else
        {
            this._navigateWithPossibleRedirect(paths.index(), toOrganization);
        }
    }

    public toProject(projectId:number):void
    {
        const path = paths.spaces(projectId);
        this._navigateTo(path);
    }

    public toPublishedApps():void
    {
        const path = paths.applications();
        this._navigateWithPossibleRedirect(path);
    }

    public toPublishedApp(urlPart:string, isBeta:boolean):void
    {
        const organizationSid = rootState.userSession.currentOrganization.siteId;
        const publishedAppUrl = PublishedAppUrlUtil.getFullUrl(organizationSid, urlPart, isBeta);
        const win = window.open(publishedAppUrl, '_blank');
        win.focus();
    }

    public toLogin():void
    {
        if( !ConfigUtil.useAppLogin() )
        {
            WindowLocationUtil.fullRedirectTo(WindowLocationUtil.getRootDomain(), '/');
        }
        else
        {
            const path = paths.login();
            this._navigateTo(path);
        }
    }

    public navigateTo(newRelativePath:string, state?:any):void
    {
        this._navigateTo(newRelativePath, state);
    }

    public setCurrentRouteProps(routeProps:RouteComponentProps<any>)
    {
        rootState.navigation.currentRouteProps = routeProps;
    }

    public getCurrentRouteProps():RouteComponentProps<any>
    {
        return rootState.navigation.currentRouteProps;
    }

    public isOnProjectPage():boolean
    {
        return this.isProjectPage(rootState.navigation.currentRouteProps.location.pathname);
    }

    public isOnHomePage():boolean
    {
        return this.isHomePage(rootState.navigation.currentRouteProps.location.pathname);
    }

    public isProjectPage(pathname:Pathname):boolean
    {
        return this._urlPathMatchesRegex(/^\/\d+$/, pathname);
    }

    public isHomePage(pathname:Pathname):boolean
    {
        return this._urlPathMatchesRegex(/^\/$/, pathname);
    }

    public isFlowEditorPage(pathname:Pathname):boolean
    {
        return this._urlPathMatchesRegex(/^\/\d+\/\d+\/flow\/\d+$/, pathname);
    }

    public getProjectId():number
    {
        const currentRouteProps = rootState.navigation.currentRouteProps;

        if( !currentRouteProps )
            return null;

        const currentPath = currentRouteProps.location.pathname;
        return Number(currentPath.replace('/', ''));
    }

    public isOnVisualDesignerPage():boolean
    {
        return false;
    }

    private _urlPathMatchesRegex(regexp:RegExp, pathname:Pathname):boolean
    {
        return regexp.test(pathname);
    }

    private _navigateWithPossibleRedirect(newRelativePath:string, organization?:Organization)
    {
        const toOrganization = organization || rootState.userSession.currentOrganization;

        if( WindowLocationUtil.isOnOrganizationHost(toOrganization) )
            this._navigateTo(newRelativePath);
        else
            WindowLocationUtil.fullRedirectToSiteId(toOrganization, newRelativePath);
    }

    private _navigateTo(newRelativePath:string, state?:any)
    {
        let history = this._getHistory();
        if( !history )
        {
            mk.error(Category.Navigation, `history object not found`);
            return;
        }

        if( WindowLocationUtil.getPath() !== newRelativePath )
        {
            // console.log(`Navigating to newRelativePath: ${newRelativePath} window.location: ${newRelativePath}`);

            history.push(newRelativePath, state);
        }
    }

    private _getHistory():History
    {
        let {currentRouteProps} = rootState.navigation;

        if( !currentRouteProps )
            return null;

        return currentRouteProps.history;
    }
}
